// The hero: a week's rota marked up with a highlighter, the way a manager checks a printed
// rota in the back office. Flagged shifts are drawn in highlighter yellow. The grid is
// decorative (aria-hidden); the two marks are explained in the caption, which is real text.
// Names are synthetic. Bars are SVG with percentage attributes, not inline styles: the page
// is prerendered (config/prerender.ts) and the CSP blocks style attributes in HTML.

import { Fragment } from 'preact';

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const DAY_START = 6; // the grid runs 06:00 to 02:00 the next morning
const DAY_HOURS = 20;

interface Bar {
  day: number;
  from: number; // hour of day, may exceed 24 for after midnight
  to: number;
  mark?: 'a' | 'b';
}

const STAFF: { name: string; bars: Bar[] }[] = [
  {
    name: 'Alex',
    bars: [
      { day: 0, from: 15, to: 23, mark: 'a' },
      { day: 1, from: 8, to: 16, mark: 'a' },
      { day: 3, from: 9, to: 17 },
      { day: 4, from: 17, to: 23 },
    ],
  },
  {
    name: 'Sam',
    bars: [
      { day: 1, from: 11, to: 19 },
      { day: 2, from: 11, to: 19 },
      { day: 5, from: 10, to: 18 },
      { day: 6, from: 10, to: 16 },
    ],
  },
  {
    name: 'Jo, 17',
    bars: [
      { day: 2, from: 18, to: 24.5, mark: 'b' },
      { day: 5, from: 12, to: 18 },
    ],
  },
];

export function RotaSketch() {
  return (
    <figure class="rota-sketch">
      <div class="rota-grid" aria-hidden="true">
        <span />
        {DAYS.map((d) => (
          <span key={d} class="rota-day">
            {d}
          </span>
        ))}
        {STAFF.map((person) => (
          <Fragment key={person.name}>
            <span class="rota-name">{person.name}</span>
            {DAYS.map((d, day) => (
              <span key={d} class="rota-cell">
                <svg class="rota-track">
                  {person.bars
                    .filter((b) => b.day === day)
                    .map((b, i) => {
                      const left = ((b.from - DAY_START) / DAY_HOURS) * 100;
                      const width = ((b.to - b.from) / DAY_HOURS) * 100;
                      const half = b.mark ? 8 : 6;
                      return (
                        <Fragment key={i}>
                          <rect
                            class={`rota-bar${b.mark ? ' is-marked' : ''}`}
                            x={`${left}%`}
                            width={`${width}%`}
                            y="50%"
                            height={half * 2}
                            transform={`translate(0 ${-half})`}
                            rx="2"
                          />
                          {b.mark && (
                            <g class="rota-pin-on-bar">
                              <circle cx={`${left + width / 2}%`} cy="5" r="11" />
                              <text x={`${left + width / 2}%`} y="5">
                                {b.mark === 'a' ? '1' : '2'}
                              </text>
                            </g>
                          )}
                        </Fragment>
                      );
                    })}
                </svg>
              </span>
            ))}
          </Fragment>
        ))}
      </div>
      <figcaption>
        <ol class="rota-notes">
          <li>
            <span class="rota-pin" aria-hidden="true">
              1
            </span>
            Alex finishes at 23:00 on Monday and is back at 08:00 on Tuesday: only 9 hours' rest.
          </li>
          <li>
            <span class="rota-pin" aria-hidden="true">
              2
            </span>
            Jo is 17 and the Wednesday shift runs past midnight.
          </li>
        </ol>
      </figcaption>
    </figure>
  );
}
